import React, { createContext, useState, useCallback, useRef } from 'react';
import { setSubscriptionAccessState, resetSubscriptionAccessState } from './subscriptionAccessStore';

export const RefreshContext = createContext();

const initialSubscriptionState = {
    subscriptionReadOnly: false,
    subscriptionExempt: false,
    subscriptionPlanExpired: false,
    subscriptionExpiringSoon: false,
    subscriptionDaysUntilExpiry: null,
    subscriptionExpiringBannerDismissed: false,
    subscriptionEndDate: null,
    subscriptionHasPlan: false,
    subscriptionCompanyId: null,
    subscriptionLoading: false,
    subscriptionPlanDetails: null,
    subscriptionQuotaStatus: null,
};

export const RefreshProvider = ({ children }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [subscriptionState, setSubscriptionState] = useState(initialSubscriptionState);
    const subscriptionReloadRef = useRef(null);

    const refreshLayout = () => {
        setRefreshKey(prevKey => prevKey + 1);
    };

    const applySubscriptionState = useCallback((partial) => {
        setSubscriptionState((prev) => {
            const next = { ...prev, ...partial };
            setSubscriptionAccessState({
                exempt: next.subscriptionExempt,
                readOnly: next.subscriptionReadOnly,
                endDate: next.subscriptionEndDate,
                companyId: next.subscriptionCompanyId,
            });
            return next;
        });
    }, []);

    const clearSubscriptionState = useCallback(() => {
        setSubscriptionState(initialSubscriptionState);
        resetSubscriptionAccessState();
    }, []);

    const setSystemAdminSubscriptionAccess = useCallback(() => {
        applySubscriptionState({
            subscriptionReadOnly: false,
            subscriptionExempt: true,
            subscriptionPlanExpired: false,
            subscriptionExpiringSoon: false,
            subscriptionDaysUntilExpiry: null,
            subscriptionExpiringBannerDismissed: false,
            subscriptionEndDate: null,
            subscriptionHasPlan: false,
            subscriptionCompanyId: null,
            subscriptionLoading: false,
            subscriptionPlanDetails: null,
            subscriptionQuotaStatus: null,
        });
    }, [applySubscriptionState]);

    const registerSubscriptionReload = useCallback((reloadFn) => {
        subscriptionReloadRef.current = reloadFn;
    }, []);

    const refreshSubscriptionState = useCallback(async () => {
        if (typeof subscriptionReloadRef.current === 'function') {
            await subscriptionReloadRef.current();
        }
    }, []);

    /** In-memory only — banner reappears after refresh or re-login */
    const dismissSubscriptionExpiringBanner = useCallback(() => {
        applySubscriptionState({ subscriptionExpiringBannerDismissed: true });
    }, [applySubscriptionState]);

    return (
        <RefreshContext.Provider value={{
            refreshKey,
            refreshLayout,
            ...subscriptionState,
            applySubscriptionState,
            clearSubscriptionState,
            setSystemAdminSubscriptionAccess,
            dismissSubscriptionExpiringBanner,
            registerSubscriptionReload,
            refreshSubscriptionState,
        }}>
            {children}
        </RefreshContext.Provider>
    );
};
